import React from "react"
import { useNavigate } from "react-router-dom"
import { reportShowPath } from "@/QuarterlyReportApp"
import { ArrowLeft } from "lucide-react"
import {
  ReportSection,
  ALWAYS_ENABLED_SECTIONS,
  VALID_SECTIONS,
} from "~/api/communications"
import { Button } from "@/ui/Button"
import QuarterlyReportLayout from "./QuarterlyReportLayout"
import PageHeader from "./PageHeader"
import PageFooter from "./PageFooter"
import { useCommunicationContext } from "./CommunicationContext"
import { useQuarterlyReportContext } from "./useQuarterlyReportContext"
import { usePdfView } from "./usePdfView"

const TableOfContents: React.FC = () => {
  const { communicationId } = useCommunicationContext()
  const { reportId, report, isLoading, error, mock } =
    useQuarterlyReportContext()
  const navigate = useNavigate()

  const enabledSections = report?.sections || []
  // Filter out Cover Letter and Table of Contents from the TOC display
  const allDisplayedSections = VALID_SECTIONS.filter((s) =>
    enabledSections.includes(s),
  )

  const { showPdfView, pdfLoading, pdfUrl, togglePdfView, loadPdfView } =
    usePdfView({
      reportId,
      sectionName: ReportSection.TABLE_OF_CONTENTS,
      editorContent: {
        section: "table_of_contents",
        sections: enabledSections,
      },
      mock,
    })

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p>Loading Table of Contents...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Failed to load report: {error.message}
      </div>
    )
  }

  if (!report) {
    return (
      <div className="alert alert-warning" role="alert">
        Report not found
      </div>
    )
  }

  return (
    <QuarterlyReportLayout
      title="Table of Contents"
      subtitle={report.household_name}
      showEditorActions={true}
      reportId={reportId}
      showPdfView={showPdfView}
      pdfLoading={pdfLoading}
      pdfUrl={pdfUrl}
      onTogglePdfView={togglePdfView}
      actions={
        <Button
          size="md"
          variant="outline"
          icon={<ArrowLeft size={18} />}
          onClick={() =>
            navigate(reportShowPath({ communicationId, reportId }))
          }
        >
          Back to Report
        </Button>
      }
      breadcrumbProps={{
        householdName: report.household_name,
        householdPath: reportShowPath({ communicationId, reportId }),
        sectionName: ReportSection.TABLE_OF_CONTENTS,
      }}
    >
      <div className="pdf-editor-container">
        <div className="pdf-dimensions-helper">
          Letter size (8.5 inches) -{" "}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault()
              loadPdfView()
            }}
            style={{ textDecoration: "underline", cursor: "pointer" }}
          >
            PDF View
          </a>
        </div>
        <div className="pdf-document">
          <PageHeader />
          <div className="pdf-content pdf-content-centered">
            <h3 className="pdf-page-section-title">Table of Contents</h3>
            <ol className="table-of-contents-list">
              {allDisplayedSections.map((section) => (
                <li key={section} className="table-of-contents-item">
                  {section}
                </li>
              ))}
            </ol>
          </div>
          <PageFooter />
        </div>
      </div>
    </QuarterlyReportLayout>
  )
}

export default TableOfContents
